/**
 * Layout.js
 *
 * Defines the overall structure of the account setup wizard.
 * Orchestrates the transition between onboarding steps and manages the layout shell.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import ParentLayout from '../Layout';
import StepAboutYou from './aboutYou/StepAboutYou';
import StepIntent from './intent/StepIntent';
import StepEducatorSubjects from './educatorSubjects/StepEducatorSubjects';
import StepPartnerExpertise from './partnerExpertise/StepPartnerExpertise';
import StepZipCode from './zipcode/StepZipCode';
import StepSite from './site/StepSite';
import {intentNext, educatorSubjectsNext, zipCodeNext} from './nextStepUtils';
import {OnboardingProvider, useOnboarding} from '@/pages/setup/account/OnboardingContext';
import OnboardingProgress from './OnboardingProgress';
import {useUser} from '@lore/auth';
import WizardButtons from '@/pages/setup/account/WizardButtons';
import Loader from '@/components/Loader';
import { useNavigate } from 'react-router-dom';

const STEPS = [
  { id: "about",       component: StepAboutYou,        next: () => "intent" },
  { id: "intent",      component: StepIntent,           next: intentNext },
  { id: "educator_subjects", component: StepEducatorSubjects, next: educatorSubjectsNext },
  { id: "partner_expertise", component: StepPartnerExpertise, next: () => "zipcode" },
  { id: "zipcode",           component: StepZipCode,           next: zipCodeNext },
  { id: "site",              component: StepSite,              next: null },
];

/**
 * WizardShell.js
 *
 * The inner wrapper for the onboarding wizard that displays the current step component.
 * It also handles the loading state.
 *
 * @returns {JSX.Element} The rendered wizard shell.
 */
function WizardShell() {
  const { activeStep, loading } = useOnboarding();
  const currentUser = useUser();

  if (loading || !activeStep) return <Loader />;

  const StepComponent = activeStep.component;

  return (
    <ParentLayout>
      <Helmet>
        <title>Setup - Account</title>
      </Helmet>
      <OnboardingProgress />
      <StepComponent key={`${activeStep.id}-${currentUser.id}`} user={currentUser}/>
      <WizardButtons/>
    </ParentLayout>
  );
}

/**
 * Entry point for the account setup wizard layout.
 *
 * Wraps the onboarding process with the Onboarding context provider. The Google
 * Maps API is provided application-wide by the APIProvider in App.js.
 *
 * @returns {JSX.Element} The rendered setup layout.
 */
export default function Layout() {
  const navigate = useNavigate();

  function handleComplete(_collectedData) {
    navigate('/');
  }

  return (
    <OnboardingProvider steps={STEPS} onComplete={handleComplete}>
      <WizardShell />
    </OnboardingProvider>
  );
}
